import { ChangeEvent, FormEvent, useRef } from "react";
import { arrayEntryAsState, State, toBrowserUrls } from "../../client";
import { FileRowInput, FileData } from "./fileRow";

interface Props {
  state: State<FileData[]>;
}

interface FormElement extends HTMLFormElement {
  url: { value: string };
  filename: { value: string };
}

export function FileInput({ state }: Props) {
  const files = state[0] ?? [];
  const setFiles = state[1];
  const input_ref = useRef<HTMLInputElement>(null);

  const appendFile = (value: FileData) => {
    setFiles([...files, value]);
  };

  const onUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files![0];
    appendFile({
      url$confidential: file,
      filename: file.name,
      type: file.type,
    });
  };

  const onSubmit = async (e: FormEvent<FormElement>) => {
    e.preventDefault();
    const target = e.target as FormElement;
    const url = target.url.value;
    const filename = target.filename.value;
    // Try fetching the type using a HEAD request.
    let type;
    try {
      // The user is adding the URL, so let's just assume that any ipfs node can
      // get it.
      const res = await fetch(toBrowserUrls(url)[0], {
        method: "HEAD",
      });
      type = res.headers.get("Content-Type") || undefined;
    } catch {
      console.warn("Could not fetch Content-Type");
    }

    appendFile({
      // Default to the url being confidential, the content not encrypted (can't
      // do that for already existing files), but name and type being public.
      url$confidential: url,
      filename,
      type,
    });

    target.url.value = "";
    target.filename.value = "";
  };

  // TODO: Maybe move this to a separate component?
  const file_rows = files.map((f, index) => (
    <FileRowInput key={index} state={arrayEntryAsState(state, index)} />
  ));

  return (
    <div>
      <div className="file-input">
        <input
          className="hidden"
          type="file"
          ref={input_ref}
          accept=".pdf,.doc,.docx,.txt,.jpg,.png,.jpeg,.zip"
          onChange={onUpload}
        />
        <div className="upload-box" onClick={() => input_ref.current?.click()}>
          Click or drag to upload
        </div>
        <div className="or-text">OR</div>
        <form onSubmit={onSubmit}>
          <input name="url" placeholder="URL" required />
          <input name="filename" placeholder="Filename" required />
          <button>Add</button>
        </form>
      </div>

      <table className="file-list">
        <thead>
          <tr>
            <th className="filename-column">Filename</th>
            <th className="confidential-column">Confidentiality</th>
            <th className="type-column">Type</th>
            <th className="preview-column">Preview</th>
            <th className="actions-column">Actions</th>
          </tr>
        </thead>
        <tbody>{file_rows}</tbody>
      </table>
    </div>
  );
}
